import { useTranslation } from 'react-i18next';
import {
  AuditHistoryFormData,
  loadMoreAuditHistoryThunk,
} from '../../../state/auditHistoryModalSlice';
import { useDispatch, useSelector } from '../../../state';
import { useEffect, useMemo, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { DataGridPro, GridColDef, useGridApiRef } from '@mui/x-data-grid-pro';
import {
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid-pro';
import { formatAction } from './helpers';
import { usePersistedDataGridColumnPreferences } from '../../../util/hooks';
import type { DataGridTableId } from '../../../util/dataGridTableIds';
import { DataGridTranslatedColumnHeader } from '../../dataGrid/DataGridTranslatedColumnHeader';

const TABLE_ID = 'audit-history' satisfies DataGridTableId;
const KEY_GLOBAL_COMMON = 'team.Global.Common';
const columnPadding = '1 2px';

function RightToolbar() {
  return (
    <GridToolbarContainer sx={{ justifyContent: 'flex-end' }}>
      <GridToolbarDensitySelector />
      <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
    </GridToolbarContainer>
  );
}

export default function ListViewTab({
  formData,
}: {
  formData: AuditHistoryFormData;
}): JSX.Element {
  const dispatch = useDispatch();
  const { t: tMA } = useTranslation([], {
    keyPrefix: 'team.Modals.AuditHistory',
  });

  const { tableLoading } = useSelector(
    ({ auditHistoryModalSlice }) => auditHistoryModalSlice,
  );

  const bottomRef = useRef<HTMLDivElement | null>(null);
  const apiRef = useGridApiRef();
  const items = formData?.results ?? [];

  // ----- Infinite scroll (same pattern as TimelineTab) -----
  useEffect(() => {
    if (!bottomRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          dispatch(loadMoreAuditHistoryThunk());
        }
      },
      {
        root: null,
        rootMargin: '300px',
        threshold: 0,
      },
    );

    observer.observe(bottomRef.current);
    return () => observer.disconnect();
  }, [dispatch]);

  const baseColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'eventId',
        headerName: 'Event Id',
        flex: 2,
        minWidth: 120,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Event Id"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
      },
      {
        field: 'createdAt',
        headerName: 'Date',
        flex: 1,
        minWidth: 140,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Date"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
        valueFormatter: (value) => dayjs(value).format('MM/DD/YYYY'),
      },
      {
        field: 'detail',
        headerName: 'Description',
        flex: 3,
        minWidth: 220,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Description"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
      },
      {
        field: 'action',
        headerName: 'Operation',
        flex: 1,
        minWidth: 160,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Operation"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
        valueFormatter: (value) => formatAction(String(value)),
      },
      {
        field: 'actorId',
        headerName: 'User',
        flex: 2,
        minWidth: 160,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="User"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
      },
    ],
    [],
  );

  const persistedPrefs = usePersistedDataGridColumnPreferences({
    tableId: TABLE_ID,
    columns: baseColumns,
    apiRef,
  });

  const columns = useMemo(
    () => persistedPrefs.applyPersistedPreferences(baseColumns),
    [baseColumns, persistedPrefs.applyPersistedPreferences],
  );

  const rows = useMemo(
    () =>
      items.map((log) => ({
        ...log,
        id: log.eventId, // DataGridPro requires `id`
      })),
    [items],
  );

  if (rows.length === 0) {
    return (
      <Box
        sx={{
          py: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography variant="body2">{tMA('No Results')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ height: 520, width: '100%', overflowX: 'auto' }}>
        <DataGridPro
          apiRef={apiRef}
          columnHeaderHeight={56}
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnSelector
          disableColumnFilter
          hideFooter
          rows={rows}
          columns={columns}
          columnVisibilityModel={persistedPrefs.columnVisibilityModel}
          onColumnVisibilityModelChange={
            persistedPrefs.onColumnVisibilityModelChange
          }
          onColumnWidthChange={persistedPrefs.onColumnWidthChange}
          onColumnOrderChange={persistedPrefs.onColumnOrderChange}
          slots={{
            toolbar: RightToolbar,
          }}
          sx={{
            border: 0,
            '& .MuiDataGrid-cell': {
              padding: columnPadding,
            },
            '& .MuiDataGrid-columnHeader': {
              padding: columnPadding,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: 1.2,
            },
          }}
          onRowsScrollEnd={() => {
            dispatch(loadMoreAuditHistoryThunk());
          }}
        />
      </Box>
      {tableLoading ? (
        <Box
          height="50px"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress size={40} />
        </Box>
      ) : null}
    </Box>
  );
}
